import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface PredictionData {
  prediction_timestamp: string;
  prediction: number;
  on_line_cmg: number | null;
  prog_cmg: number | null;
  is_gated: boolean;
  zero_probability: number;
  zero_threshold: number;
  lag_baseline: number;
  postprocessed_prediction: number;
  blended_prediction: number;
  alpha: number;
  error: string | null;
}
interface ApiResponse {
  request_id: string;
  forecast_for_date: string;
  generated_at: string;
  processing_time_seconds: number;
  summary: {
    min_cost: number;
    max_cost: number;
    avg_cost: number;
  };
  predictions: PredictionData[];
  successful_predictions: number;
  failed_predictions: number;
}
interface ChartDataPoint {
  time: string;
  online: number | null;
  programmed: number | null;
  forecast: number;
}
export function EnergyChart() {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    forecastAvg: number;
    programmedAvg: number;
    improvement: number;
  } | null>(null);
  // Fixed date: November 6, 2024
  const DEMO_DATE = '2025-11-06';
  useEffect(() => {
    const fetchPredictions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('https://api.vectrei.com/predict/day', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prediction_date: DEMO_DATE
          })
        });
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        const data: ApiResponse = await response.json();
        // Transform API data to chart format
        // Sample every 4th point to get 24 hourly points instead of 96 15-min intervals
        const transformedData: ChartDataPoint[] = data.predictions.filter((_, index) => index % 4 === 0) // Every hour
        .map(pred => {
          const date = new Date(pred.prediction_timestamp);
          const hours = date.getHours();
          const time = `${hours.toString().padStart(2, '0')}:00`;
          return {
            time,
            online: pred.on_line_cmg,
            programmed: pred.prog_cmg,
            forecast: pred.prediction
          };
        });
        setChartData(transformedData);
        // Calculate summary statistics
        const validProgrammed = data.predictions.filter(p => p.prog_cmg !== null);
        const programmedAvg = validProgrammed.length > 0 ? validProgrammed.reduce((sum, p) => sum + (p.prog_cmg || 0), 0) / validProgrammed.length : 0;
        const forecastAvg = data.predictions.reduce((sum, p) => sum + p.prediction, 0) / data.predictions.length;
        // Improvement: positive when our forecast is lower (better) than programmed
        const improvement = programmedAvg > 0 ? (programmedAvg - forecastAvg) / programmedAvg * 100 : 0;
        setSummary({
          forecastAvg,
          programmedAvg,
          improvement
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar datos');
        console.error('Error fetching predictions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPredictions();
  }, []);
  return <div className="relative p-8 bg-white corner-accent mb-16" data-id="element-112">
      <div className="mb-6" data-id="element-113">
        <div className="flex justify-between items-start mb-4" data-id="element-114">
          <div data-id="element-115">
            <div className="text-xs uppercase tracking-wider mb-2 text-gray-600" data-id="element-116">
              RENDIMIENTO DE OPTIMIZACIÓN
            </div>
            <h3 className="text-2xl font-bold mb-2" data-id="element-117">PRONÓSTICO MERCADO</h3>
            <p className="text-sm text-gray-700" data-id="element-118">
              Comparación: CMG Real vs Programado vs Nuestra Proyección - 6 de
              Noviembre 2025
            </p>
          </div>
          {summary && <div className="relative p-4 bg-gray-50 corner-accent" data-id="element-119">
              <div className="text-xs uppercase tracking-wider mb-2 text-gray-600" data-id="element-120">
                MEJORA VS PROGRAMADO
              </div>
              <div className={`text-3xl font-bold ${summary.improvement > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-121">
                {summary.improvement > 0 && '+'}
                {summary.improvement.toFixed(1)}%
              </div>
            </div>}
        </div>
      </div>

      {loading && <div className="h-80 flex items-center justify-center" data-id="element-122">
          <div className="text-sm uppercase tracking-wider text-gray-600" data-id="element-123">
            CARGANDO DATOS...
          </div>
        </div>}

      {error && <div className="h-80 flex items-center justify-center" data-id="element-124">
          <div className="text-sm text-red-600" data-id="element-125">ERROR: {error}</div>
        </div>}

      {!loading && !error && chartData.length > 0 && <>
          <div className="h-80" data-id="element-126">
            <ResponsiveContainer width="100%" height="100%" data-id="element-127">
              <LineChart data={chartData} margin={{
            top: 5,
            right: 5,
            left: 5,
            bottom: 5
          }} data-id="element-128">
                <CartesianGrid strokeDasharray="0" stroke="#D1D5DB" vertical={false} data-id="element-129" />
                <XAxis dataKey="time" stroke="#000000" tick={{
              fill: '#000000',
              fontSize: 12,
              fontFamily: 'monospace'
            }} tickLine={false} axisLine={{
              stroke: '#000000',
              strokeWidth: 1
            }} data-id="element-130" />
                <YAxis stroke="#000000" tick={{
              fill: '#000000',
              fontSize: 12,
              fontFamily: 'monospace'
            }} tickLine={false} axisLine={{
              stroke: '#000000',
              strokeWidth: 1
            }} label={{
              value: 'COSTO CMg',
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#000000',
                fontSize: 12,
                fontFamily: 'monospace'
              }
            }} data-id="element-131" />
                <Tooltip contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #000000',
              borderRadius: 0,
              fontFamily: 'monospace',
              fontSize: 12
            }} labelStyle={{
              color: '#000000',
              fontWeight: 'bold'
            }} formatter={(value: number | null) => value !== null ? value.toFixed(2) : 'N/A'} data-id="element-132" />
                <Line type="monotone" dataKey="online" stroke="#9CA3AF" strokeWidth={2} dot={false} name="CMG Real (Online)" connectNulls data-id="element-133" />
                <Line type="monotone" dataKey="programmed" stroke="#EF4444" strokeWidth={2} dot={false} name="CMG Programado" strokeDasharray="5 5" connectNulls data-id="element-134" />
                <Line type="monotone" dataKey="forecast" stroke="#16A34A" strokeWidth={3} dot={false} name="Nuestra Predicción" data-id="element-135" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 flex gap-6 text-sm flex-wrap" data-id="element-136">
            <div className="flex items-center gap-2" data-id="element-137">
              <div className="w-8 h-0.5 bg-gray-400" data-id="element-138"></div>
              <span className="uppercase tracking-wider text-gray-600" data-id="element-139">
                CMG REAL (ONLINE)
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-140">
              <div className="w-8 h-0.5 bg-red-500" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, #EF4444 0, #EF4444 5px, transparent 5px, transparent 10px)'
          }} data-id="element-141"></div>
              <span className="uppercase tracking-wider text-red-600" data-id="element-142">
                CMG PROGRAMADO
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-143">
              <div className="w-8 h-0.5 bg-green-600" data-id="element-144"></div>
              <span className="uppercase tracking-wider font-bold text-green-600" data-id="element-145">
                NUESTRA PREDICCIÓN
              </span>
            </div>
          </div>
        </>}
    </div>;
}